import { AIChatBox } from "@/components/ai-chatbox";
import { AlertDialogPro } from "@/components/ui/alert-dialog";
import { useMobile } from "@/hooks/useMedia";
import { tryResponse } from "@/lib/request";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { useStore } from "@tanstack/react-form";
import { Block, createFileRoute } from "@tanstack/react-router";
import { useRequest } from "ahooks";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
	createCreation,
	getCreation,
	getPreviewDialogue,
	publishCreation,
	refreshPreviewDialogue,
	updateCreation,
} from "./-comps/api";
import { BaseBtn } from "./-comps/buttons";
import {
	MAX_BRIEF_INTRODUCTION_LENGTH,
	MAX_TITLE_LENGTH,
	formOpts,
	useFieldContext,
} from "./-comps/form-context";
import {
	CreateGuideIcon,
	ExportIcon,
	ImportIcon,
	PreviewIcon,
	PublishIcon,
	SaveIcon,
} from "./-comps/icons";
import { VForm, useAppForm } from "./-comps/vform";

export const Route = createFileRoute("/$lang/edit/$id")({
	component: RouteComponent,
	validateSearch: (
		search,
	): {
		preview_dialogue_id?: string;
		preview_time?: number;
	} => {
		return {
			preview_dialogue_id: search.preview_dialogue_id as string,
			preview_time: search.preview_time as number,
		};
	},
});

function RouteComponent() {
	const { id } = Route.useParams();
	const { preview_dialogue_id, preview_time } = Route.useSearch();
	const { data: creationDetail, mutate: mutateCreationDetail } = useRequest(
		() => {
			return tryResponse(getCreation(id));
		},
		{
			ready: !!id && id !== "new",
		},
	);

	const [importData, setImportData] = useState<any>(null);

	const saveDraftRef = useRef<any>(null);
	async function doSaveDraft() {
		const titleErrors = await form.validateField("title", "change");
		const descriptionErrors = await form.validateField(
			"brief_introduction",
			"change",
		);
		if (titleErrors.length > 0 || descriptionErrors.length > 0) {
			return;
		}
		return createOrUpdate({ ...form.state.values, preview: false }).then(
			(dt) => {
				saveDraftRef.current = form.state.values;
				toast.success(m.edit_save_draft_success());
				if (id === "new") {
					return nav({
						to: "/$lang/edit/$id",
						params: {
							id: dt.creation_id,
						},
					});
				}
			},
		);
	}

	const form = useAppForm({
		...formOpts,
		defaultValues: {
			...formOpts.defaultValues,
			...(creationDetail || {}),
			...(importData || {}),
		},
		onSubmitMeta: {} as {
			submit_type: "publish" | "preview";
		},
		onSubmit: async ({ value, meta }) => {
			if (meta.submit_type === "publish") {
				await createOrUpdate({ ...value, preview: false }).then(async (dt) => {
					if (creationDetail?.status !== "PUBLISHED") {
						await tryResponse(publishCreation(dt.creation_id));
					}
					toast.success(m.common_published());
					return nav({
						to: "/$lang/story/$creation_id/$dialogue_id",
						params: {
							creation_id: dt.creation_id,
							dialogue_id: "-",
						},
					});
				});
			} else if (meta.submit_type === "preview") {
				await createOrUpdate({ ...value, preview: true }).then(async (dt) => {
					const creation_id = dt.creation_id;
					// 预览必须是update里会生效，create里不会生效
					if (id === "new") {
						await tryResponse(
							updateCreation({
								...value,
								preview: true,
								creation_id,
							}),
						);
					}
					return tryResponse(getPreviewDialogue(creation_id)).then(
						async (jt) => {
							await tryResponse(refreshPreviewDialogue(creation_id));
							setPreviewAIChatInfo({
								prologue: value.prologue,
								cover_image: value.cover_image,
								background_image: value.background_image,
								detailed_introduction: value.detailed_introduction || "",
							});
							toast.success(m.edit_preview_generated());
							document.getElementById("chat-box-wrapper")?.scrollIntoView({
								behavior: "smooth",
							});
							nav({
								to: "/$lang/edit/$id",
								params: {
									id: creation_id,
								},
								search: {
									preview_dialogue_id: jt?.dialogue_id,
									preview_time: Date.now(),
								},
							});
						},
					);
				});
			}
		},
		onSubmitInvalid: ({ value }) => {
			toast.error(m.edit_verify_info());
			if (
				!(value?.title?.length > 0 && value?.title?.length <= MAX_TITLE_LENGTH)
			) {
				document.querySelector("#scroll_title")?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
				return;
			}
			if (!(value?.tags?.length > 0)) {
				document.querySelector("#scroll_tags")?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
				return;
			}
			if (
				!(
					value?.brief_introduction?.length > 0 &&
					value?.brief_introduction?.length <= MAX_BRIEF_INTRODUCTION_LENGTH
				)
			) {
				document.querySelector("#scroll_brief_introduction")?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
				return;
			}
			if (!(value?.cover_image?.length > 0)) {
				document.querySelector("#scroll_cover_image")?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
				return;
			}
			if (!(value?.system_rule?.length > 0)) {
				document.querySelector("#scroll_system_rule")?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
				return;
			}
		},
	});

	useEffect(() => {
		if (id === "new") {
			mutateCreationDetail(null);
			form.reset();
		}
	}, [id]);

	const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
	const isDirty = useStore(form.store, (state) => state.isDirty);

	async function createOrUpdate(params: any) {
		if (id === "new") {
			if (!isDirty && !importData) {
				toast.error(m.edit_save_draft_tip());
				throw new Error("isDirty");
			}
			return tryResponse(createCreation(params));
		}
		const newParams = {
			...params,
			creation_id: id,
		};
		await tryResponse(updateCreation(newParams));
		return newParams;
	}

	const nav = Route.useNavigate();

	const [previewAIChatInfo, setPreviewAIChatInfo] = useState({
		prologue: "",
		cover_image: "",
		background_image: "",
		detailed_introduction: "",
	});

	const [defaultModelData, setDefaultModelData] = useState<{
		model: string;
		gem: number;
		token: number;
		provider_recommend: boolean;
	} | null>(null);

	const isMobile = useMobile();

	return (
		<div className="mx-auto max-w-6xl text-sm md:px-0 px-4 md:pb-0 pb-24">
			<Block
				shouldBlockFn={({ next }) => {
					if (importData) {
						return true;
					}
					if (
						saveDraftRef.current &&
						JSON.stringify(saveDraftRef.current) ===
							JSON.stringify(form.state.values)
					) {
						return false;
					}
					if (
						creationDetail?.status === "PUBLISHED" ||
						creationDetail?.status === "UNPUBLISHED"
					) {
						return false;
					}
					if (
						next.fullPath === "/$lang/story/$creation_id/$dialogue_id" ||
						next.fullPath === "/$lang/edit/$id"
					) {
						return false;
					}
					return !isDefaultValue;
				}}
				withResolver
			>
				{({ status, proceed, reset }) => (
					<>
						{status === "blocked" && (
							<AlertDialogPro
								open={status === "blocked"}
								content={
									<div className="flex flex-col items-center gap-[6px]">
										<div className="mb-2 w-[120px] h-[120px]">
											<img
												className="w-full h-full object-cover"
												src="/assets/icons/edit.svg"
												alt="creation"
											/>
										</div>
										<div className="text-t1 text-base font-normal pb-4">
											{m.edit_save_draft()}
										</div>
									</div>
								}
								onCancel={(cancelType) => {
									if (cancelType === "close_icon") {
										reset?.();
									} else {
										proceed();
									}
								}}
								onConfirm={async () => {
									await doSaveDraft().then(() => {
										proceed();
									});
								}}
								confirmText={m.common_save()}
								cancelText={m.common_unsave()}
								loading={false}
							/>
						)}
					</>
				)}
			</Block>
			<div className="text-main1 text-sm flex items-center gap-1">
				<img
					src="/assets/images/write-tip.png"
					alt="write-tip"
					className="size-8"
				/>
				{m.edit_author_reward()}
			</div>

			<div className="flex mt-3">
				<div className="flex-1 gap-2 grid grid-cols-3 md:flex">
					<BaseBtn
						onClick={() => {
							window.open(
								"https://peachlust.notion.site/Content-Creation-Guidelines-22d48a18e92880bda2cdd930952510c3",
								"_blank",
							);
						}}
					>
						<CreateGuideIcon className="flex-shrink-0" />
						{m.edit_create_guide()}
					</BaseBtn>
					<BaseBtn
						onClick={() => {
							const {
								creation_id,
								status,
								popularity,
								view_count,
								paid_user_count,
								gem_consumption_count,
								created_time,
								updated_time,
								latest_edit_time,
								// 上述字段不导出
								...rest
							} = form.state.values;

							const fileBlob = new Blob([JSON.stringify(rest, null, 2)], {
								type: "application/json",
							});
							const objectURL = window.URL.createObjectURL(fileBlob);
							const a = document.createElement("a");
							a.href = objectURL;
							a.download = `PeachLust_${dayjs().format("MMDDHHmm")}.json`;
							a.click();
							window.URL.revokeObjectURL(objectURL);
						}}
					>
						<ExportIcon className="flex-shrink-0" />
						{isMobile ? m.edit_export_config_simple() : m.edit_export_config()}
					</BaseBtn>
					<BaseBtn
						onClick={() => {
							const file = document.createElement("input");
							file.type = "file";
							file.accept = "application/json";
							file.onchange = (e) => {
								const file = (e.target as HTMLInputElement).files?.[0];
								if (file) {
									const reader = new FileReader();
									reader.onload = (e) => {
										try {
											const data = JSON.parse(e.target?.result as string);
											setImportData(data || {});
											form.reset();
										} catch (error) {
											toast.error(m.edit_import_failed());
										}
									};
									reader.readAsText(file);
								}
							};
							file.click();
						}}
					>
						<ImportIcon className="flex-shrink-0" />
						{isMobile ? m.edit_import_config_simple() : m.edit_import_config()}
					</BaseBtn>
				</div>
				<div
					className={cn(
						"md:flex grid grid-cols-3 gap-2 md:relative absolute right-0 bottom-6 md:bottom-auto md:right-auto w-full z-50 md:w-auto px-4 md:px-0",
						isMobile &&
							(creationDetail?.status === "PUBLISHED" ||
								creationDetail?.status === "UNPUBLISHED") &&
							"grid-cols-2",
					)}
				>
					{creationDetail?.status === "PUBLISHED" ||
					creationDetail?.status === "UNPUBLISHED" ? null : (
						<BaseBtn
							className="bg-c2"
							icon={<SaveIcon />}
							onClick={async () => {
								await doSaveDraft();
							}}
						>
							{m.common_save()}
						</BaseBtn>
					)}
					<BaseBtn
						icon={<PreviewIcon />}
						onClick={async () => {
							await form.handleSubmit({
								submit_type: "preview",
							});
						}}
						className="bg-c2"
					>
						{m.edit_preview()}
					</BaseBtn>
					<BaseBtn
						icon={<PublishIcon />}
						onClick={async () => {
							await form.handleSubmit({
								submit_type: "publish",
							});
						}}
						className="bg-c2"
					>
						{m.edit_publish()}
					</BaseBtn>
				</div>
			</div>

			<div className="flex gap-1 flex-1 mt-3.5 flex-wrap md:flex-nowrap">
				<div className="flex-1 md:max-h-[calc(100vh-190px)] md:overflow-y-auto pb-2 md:pr-4">
					<form.AppField name="edit_mode" children={() => <Tabs />} />
					<VForm form={form} />
				</div>
				<AIChatBox
					detailed_introduction={previewAIChatInfo.detailed_introduction || ""}
					className="md:max-h-[calc(100vh-190px)] h-[80vh] md:h-auto"
					key={preview_time}
					creation_id={id === "new" ? "" : id}
					dialogue_id={preview_dialogue_id || ""}
					isPreview={true}
					isShare={false}
					prologue={previewAIChatInfo.prologue}
					cover_image={previewAIChatInfo.cover_image}
					background_image={previewAIChatInfo.background_image}
					onPreview={async () => {
						await form.handleSubmit({
							submit_type: "preview",
						});
					}}
					defaultModelData={defaultModelData}
					setDefaultModelData={setDefaultModelData}
				/>
			</div>
		</div>
	);
}

function Tabs() {
	const field = useFieldContext<string>();
	return (
		<div className="flex gap-[1px] *:flex-1 cursor-pointer *:pb-1 *:border-b-[1px] *:border-b-t3">
			<div
				className={cn(
					"flex flex-col items-center",
					field.state.value === "SIMPLE" && "!border-b-main1 !border-b-[2px]",
				)}
				onClick={() => field.handleChange("SIMPLE")}
			>
				<div className="text-base">{m.edit_simple()}</div>
				<div className="text-xs text-t2">（{m.edit_simple_desc()}）</div>
			</div>
			<div
				className={cn(
					"flex flex-col items-center",
					field.state.value === "ADVANCED" && "!border-b-main1 !border-b-[2px]",
				)}
				onClick={() => field.handleChange("ADVANCED")}
			>
				<div className="text-base">{m.edit_advanced()}</div>
				<div className="text-xs text-t2">（{m.edit_advanced_desc()}）</div>
			</div>
		</div>
	);
}
